// src/components/ReplyModal.js
import React, { useEffect, useRef, useState } from "react";
import {
  getTimelineConfig,
  listReplies,
  addReply,
  editReply,
  softDeleteReply,
  canShowKebab,
  normalizeDate,
} from "../services/timelineService";
import nomesBonitosCategoria from "../utils/nomesBonitosCategoria";
import "./ReplyModal.css";
import XPopup from "./XPopup";

/* ===== Spoiler inline ===== */
function textHasInlineSpoiler(t = "") {
  return /\[spoiler\][\s\S]*?\[\/spoiler\]/i.test(String(t || ""));
}
function InlineSpoiler({ children }) {
  const [open, setOpen] = useState(false);
  return (
    <span
      className={`spoiler-inline ${open ? "revealed" : ""}`}
      role="button"
      tabIndex={0}
      onClick={() => setOpen((v) => !v)}
      onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && setOpen((v) => !v)}
      aria-label={open ? "Ocultar spoiler" : "Revelar spoiler"}
    >
      {children}
    </span>
  );
}
function renderWithSpoilers(text) {
  if (!text) return null;
  return String(text).split("\n").map((line, i) => {
    const parts = [];
    let last = 0;
    const re = /\[spoiler\]([\s\S]*?)\[\/spoiler\]/gi;
    let m;
    while ((m = re.exec(line)) !== null) {
      const before = line.slice(last, m.index);
      if (before) parts.push(before);
      parts.push(<InlineSpoiler key={`sp-r-${i}-${m.index}`}>{m[1] || ""}</InlineSpoiler>);
      last = re.lastIndex;
    }
    const tail = line.slice(last);
    if (tail) parts.push(tail);
    return <p key={i}>{parts.length ? parts : line}</p>;
  });
}

/* ===== helpers ===== */
function renderPlain(text) {
  return (text || "").split("\n").map((line, i) => (
    <p key={i}>
      {line.split(/(\*\*.*?\*\*)/g).map((chunk, j) => {
        if (/^\*\*.*\*\*$/.test(chunk)) {
          return <strong key={j}>{chunk.replace(/^\*\*|\*\*$/g, "")}</strong>;
        }
        return <span key={j}>{chunk}</span>;
      })}
    </p>
  ));
}
function formatPtDate(ts) {
  try {
    const d = normalizeDate(ts);
    const dia = d.toLocaleDateString("pt-PT", { day: "2-digit" });
    const mes = d.toLocaleDateString("pt-PT", { month: "long" });
    const hora = d.toLocaleTimeString("pt-PT", { hour: "2-digit", minute: "2-digit" });
    return `${dia} de ${mes}, às ${hora}`;
  } catch {
    return "";
  }
}
function prettyModo(m) {
  if (m === "oscar-indicados") return "Indicados ao Oscar";
  if (m === "oscar-vencedores") return "Vencedores do Oscar";
  if (m === "globodeouro-indicados") return "Indicados ao Globo de Ouro";
  if (m === "globodeouro-vencedores") return "Vencedores do Globo de Ouro";
  if (m === "sagawards-indicados") return "Indicados ao SAG Awards";
  if (m === "sagawards-vencedores") return "Vencedores do SAG Awards";
  if (m === "criticschoice-indicados") return "Indicados ao Critics Choice";
  if (m === "criticschoice-vencedores") return "Vencedores do Critics Choice";
  if (m === "dga-indicados") return "Indicados ao DGA Awards";
  if (m === "dga-vencedoress") return "Vencedores do DGA Awards";
  if (m === "pga-indicados") return "Indicados ao PGA Awards";
  if (m === "pga-vencedores") return "Vencedores do PGA Awards";
  if (m === "bafta-indicados") return "Indicados ao BAFTA";
  if (m === "bafta-vencedores") return "Vencedores do BAFTA";
  return m || "";
}
function prettyCategoria(c) {
  if (!c) return "";
  const key = String(c).toLowerCase().replace(/\s+/g, "");
  return nomesBonitosCategoria[key] || c;
}
function medalClassFor(pos, acertouRaw) {
  const errou = acertouRaw === false || acertouRaw === "false" || acertouRaw === 0;
  if (errou) return "cor-5err";
  if (pos === 1) return "cor-1";
  if (pos === 2) return "cor-2";
  if (pos === 3 || pos === 4) return "cor-3";
  return "cor-5";
}

/* ===== Componente de header do post ===== */
function PostHeaderCompact({ post }) {
  const goProfile = () => {
    if (!post.authorUid) return;
    window.location.href = `#/perfil-publico/${post.authorUid}`;
  };
  return (
    <header className="rm-post-head">
      <button className="rm-avatar" onClick={goProfile} title="Ver perfil" type="button">
        {post.authorAvatar ? (
          <img src={post.authorAvatar} alt={post.authorName || ""} />
        ) : (
          <div className="rm-avatar ph" />
        )}
      </button>
      <div className="rm-head-main">
        <div className="rm-name-row">
          {post.especialista && <span className="rm-badge">ESPECIALISTA OSCAR</span>}
          <button className="rm-author" onClick={goProfile} title="Ver perfil" type="button">
            {post.authorName}
          </button>
        </div>
      </div>
    </header>
  );
}

/* ===== Hook de marcador para replies ===== */
function useSpoilerMarker(textRef, value, setValue) {
  const [marking, setMarking] = useState(false);

  function wrapSelection() {
    const ta = textRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    if (start === end) return;
    const before = value.slice(0, start);
    const sel = value.slice(start, end);
    const after = value.slice(end);
    const already = /^\[spoiler\][\s\S]*\[\/spoiler\]$/.test(sel);
    const wrapped = already ? sel.replace(/^\[spoiler\]|\[\/spoiler\]$/g, "") : `[spoiler]${sel}[/spoiler]`;
    const next = before + wrapped + after;
    setValue(next);
    const caret = before.length + wrapped.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }
  function unwrapSelection() {
    const ta = textRef.current;
    if (!ta) return;
    const start = ta.selectionStart ?? 0;
    const end = ta.selectionEnd ?? 0;
    if (start === end) return;
    const before = value.slice(0, start);
    let sel = value.slice(start, end);
    const after = value.slice(end);
    sel = sel.replace(/\[spoiler\]/gi, "").replace(/\[\/spoiler\]/gi, "");
    const next = before + sel + after;
    setValue(next);
    const caret = before.length + sel.length;
    requestAnimationFrame(() => {
      ta.focus();
      ta.setSelectionRange(caret, caret);
    });
  }
  useEffect(() => {
    function onKey(e) {
      if (!marking) return;
      const meta = e.ctrlKey || e.metaKey;
      if (meta && e.key.toLowerCase() === "m") {
        e.preventDefault();
        wrapSelection();
      }
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [marking]);
  return { marking, start: () => setMarking(true), finish: () => setMarking(false), wrapSelection, unwrapSelection };
}

const ICONS = {
  reply: "https://i.imgur.com/SPqgsR9.png",
  arrowOverflow: "https://i.imgur.com/7B3fBYd.png",
};

export default function ReplyModal({
  post,
  currentUid,
  currentUser,
  onClose,
  onCloseTimeline,
}) {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [replies, setReplies] = useState([]);

  const [text, setText] = useState("");
  const [replyHasSpoiler, setReplyHasSpoiler] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const [editingId, setEditingId] = useState(null);
  const [editingText, setEditingText] = useState("");
  const [editingSpoiler, setEditingSpoiler] = useState(false);

  const scrollerRef = useRef(null);
  const [hasOverflow, setHasOverflow] = useState(false);
  const textareaRef = useRef(null);
  const editRef = useRef(null);

  // estado do popup
  const [popup, setPopup] = useState({
    open: false,
    tipo: "info",
    mensagem: "",
    onConfirm: null,
  });

  const marker = useSpoilerMarker(textareaRef, text, setText);
  const markerEdit = useSpoilerMarker(editRef, editingText, setEditingText);

  // ---------- kebabs ----------
  function closeAllKebabs() {
    document.querySelectorAll(".rm-kebab.open").forEach((k) => k.classList.remove("open"));
    document.querySelectorAll(".rm-kebab-menu.open").forEach((m) => m.classList.remove("open"));
  }
  useEffect(() => {
    const onDown = (e) => { if (!e.target.closest?.(".rm-kebab")) closeAllKebabs(); };
    const onKey = (e) => { if (e.key === "Escape") closeAllKebabs(); };
    window.addEventListener("mousedown", onDown);
    window.addEventListener("keydown", onKey);
    return () => {
      window.removeEventListener("mousedown", onDown);
      window.removeEventListener("keydown", onKey);
    };
  }, []);
  // ---------------------------

  useEffect(() => {
    const el = scrollerRef.current;
    if (!el) return;
    const check = () => setHasOverflow(el.scrollWidth > el.clientWidth + 2);
    check();
    const ro = new ResizeObserver(check);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const [c, list] = await Promise.all([
          getTimelineConfig(),
          listReplies({ postId: post.postId }),
        ]);
        if (!mounted) return;
        setCfg(c);
        setReplies(list);
        window.dispatchEvent(new CustomEvent("tl:reply-count", {
          detail: { postId: post.postId, count: list?.length ?? 0 },
        }));
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, [post.postId]);

  async function repullAndBroadcast() {
    const list = await listReplies({ postId: post.postId });
    setReplies(list);
    window.dispatchEvent(new CustomEvent("tl:reply-count", {
      detail: { postId: post.postId, count: list?.length ?? 0 },
    }));
  }

  async function handlePublishReply() {
    if (!currentUser?.uid) return;
    if (!text.trim()) return;
    setSubmitting(true);
    try {
      const finalHas = replyHasSpoiler || textHasInlineSpoiler(text);
      await addReply({
        postId: post.postId,
        author: {
          uid: currentUser.uid,
          name: currentUser.name,
          avatar: currentUser.avatar,
          especialista: !!currentUser.especialista,
        },
        text,
        postingMode: cfg?.postingMode || "auto",
        hasSpoiler: finalHas,
      });
      setText("");
      await repullAndBroadcast();
      requestAnimationFrame(() => textareaRef.current?.focus());
    } catch (e) {
      console.error(e);
      setPopup({ open: true, tipo: "info", mensagem: e.message || "Não foi possível publicar seu comentário.", onConfirm: null });
    } finally {
      setSubmitting(false);
    }
  }

  async function handleSaveEdit() {
    if (!editingId || !editingText.trim()) {
      setEditingId(null);
      setEditingText("");
      return;
    }
    try {
      const finalHas = editingSpoiler || textHasInlineSpoiler(editingText);
      await editReply({ postId: post.postId, replyId: editingId, newText: editingText, hasSpoiler: finalHas });
      setEditingId(null);
      setEditingText("");
      await repullAndBroadcast();
    } catch (e) {
      console.error(e);
      setPopup({ open: true, tipo: "info", mensagem: "Não foi possível editar o comentário.", onConfirm: null });
    }
  }

  async function handleDeleteReply(replyId) {
    setPopup({
      open: true,
      tipo: "confirm",
      mensagem: "Apagar este comentário?",
      onConfirm: async () => {
        setPopup({ open: false, tipo: "info", mensagem: "", onConfirm: null });
        try {
          await softDeleteReply({ postId: post.postId, replyId });
          await repullAndBroadcast();
        } catch (e) {
          console.error(e);
          setPopup({ open: true, tipo: "info", mensagem: "Não foi possível apagar o comentário.", onConfirm: null });
        }
      },
    });
  }

  function handleKeyDown(e) {
    const meta = e.ctrlKey || e.metaKey;
    if (meta && e.key === "Enter" && !submitting) {
      e.preventDefault();
      handlePublishReply();
    }
  }

  return (
    <div className="rm-modal">
      <div className="rm-inner">
        {/* Post original */}
        <div className="rm-post">
          <PostHeaderCompact post={post} />
          {post.text && (
            <div className="rm-post-text">
              {textHasInlineSpoiler(post.text)
                ? renderWithSpoilers(post.text)
                : renderPlain(post.text)}
            </div>
          )}

          {post.attach && (
            <section className="tl-attach rm-attach">
              <div className="tl-attach-head">
                <div className="tl-attach-mode">Apostas – {prettyModo(post.attach.modo)}</div>
                <div className="tl-attach-cat">
                  {(prettyCategoria(post.attach.categoria) || "").toUpperCase()}
                </div>
              </div>

              <div className="tl-carousel-wrap">
                <div className="tl-carousel" ref={scrollerRef}>
                  {(post.attach.itens || []).map((item, idx) => {
                    const pos = idx + 1;
                    const acertou =
                      item?.acertou === true || item?.acertou === "true" || item?.acertou === 1;
                    const errou =
                      item?.acertou === false || item?.acertou === "false" || item?.acertou === 0;

                    return (
                      <div className="tl-item" key={`${item.titulo}-${idx}`}>
                        <div className={`tl-poster ${errou ? "desbotado" : ""}`}>
                          <img src={item.imagem || "https://i.imgur.com/6MbQoCt.png"} alt={item.titulo || ""} />
                          {acertou && <span className="tl-hit-badge">ACERTEI</span>}
                          <div className={`tl-medal ${medalClassFor(pos, item?.acertou)}`}>
                            <span>{pos}º</span>
                          </div>
                        </div>

                        <div className="tl-item-info">
                          <strong className="tl-item-title">{item.titulo}</strong>
                          {item.filme && <div className="tl-item-sub">{item.filme}</div>}
                          {item.pais && !item.filme && <div className="tl-item-sub">{item.pais}</div>}
                        </div>
                      </div>
                    );
                  })}
                </div>

                <div className="tl-overflow-hint" aria-hidden="true" style={{ display: hasOverflow ? "block" : "none" }}>
                  <img src={ICONS.arrowOverflow} alt="" />
                </div>
              </div>
            </section>
          )}
          <div className="rm-date">{formatPtDate(post.createdAt || post.createdAtMs)}</div>
        </div>

        {/* Lista de replies */}
        <div className="rm-replies">
          {loading ? (
            <div className="rm-loading">Carregando…</div>
          ) : replies.length === 0 ? (
            <div className="rm-empty">
              <img className="rm-empty-icon" src={ICONS.reply} alt="" />
              <div>Sem comentários ainda.</div>
            </div>
          ) : (
            replies.map((r) => (
              <div className="rm-reply" key={r.id}>
                <div className="rm-reply-avatar">
                  {r.authorAvatar ? (
                    <img src={r.authorAvatar} alt={r.authorName} />
                  ) : (
                    <div className="rm-avatar ph" />
                  )}
                </div>

                <div className="rm-reply-body">
                  <div className="rm-reply-head">
                    <div className="rm-reply-name">
                      {r.especialista && <span className="rm-badge">ESPECIALISTA OSCAR</span>}
                      <span className="rm-author">{r.authorName}</span>
                      <div className="rm-date2">{formatPtDate(r.createdAt || r.createdAtMs)}</div>
                    </div>
                  </div>

                  {editingId === r.id ? (
                    <div className="rm-edit-box">
                      <textarea
                        ref={editRef}
                        className={markerEdit.marking ? "spoiler-marking" : ""}
                        value={editingText}
                        onChange={(e) => setEditingText(e.target.value)}
                        placeholder="Edite seu comentário…"
                      />
                      <div className="rm-edit-controls">
                        <label className="rm-check">
                          <input type="checkbox" checked={editingSpoiler} onChange={(e) => setEditingSpoiler(e.target.checked)} />
                          <span>Este comentário contém <strong>spoilers</strong> (para esconder tudo)</span>
                        </label>
                        <div className="rm-marker">
                          {!markerEdit.marking ? (
                            <button className="rm-marker-btn" onClick={markerEdit.start}>Marcador de Spoiler</button>
                          ) : (
                            <div className="rm-marker-on">
                              <span className="rm-marker-hint">use o cursor para selecionar os trechos e clique “Aplicar à seleção”</span>
                              <button className="rm-marker-finish" onClick={markerEdit.finish}>Terminei de Marcar</button>
                              <div className="rm-marker-tools">
                                <button onClick={markerEdit.wrapSelection}>Aplicar à seleção</button>
                                <button onClick={markerEdit.unwrapSelection}>Remover da seleção</button>
                              </div>
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="rm-edit-actions">
                        <button className="rm-save" onClick={handleSaveEdit}>Salvar</button>
                        <button
                          className="rm-cancel"
                          onClick={() => {
                            setEditingId(null);
                            setEditingText("");
                          }}
                        >
                          Cancelar
                        </button>
                      </div>
                    </div>
                  ) : (
                    <div className="rm-reply-text">
                      {textHasInlineSpoiler(r.text) ? renderWithSpoilers(r.text) : renderPlain(r.text)}
                    </div>
                  )}

                  {canShowKebab({ currentUid, authorUid: r.authorUid }) && (
                    <div className="rm-kebab">
                      <button
                        className="rm-kebab-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          closeAllKebabs();
                          const wrapper = e.currentTarget.closest(".rm-kebab");
                          const menu = e.currentTarget.nextElementSibling;
                          wrapper?.classList.toggle("open");
                          menu?.classList.toggle("open");
                        }}
                      >
                        ⋯
                      </button>
                      <div className="rm-kebab-menu">
                        <button
                          onClick={() => {
                            closeAllKebabs();
                            setEditingId(r.id);
                            setEditingText(r.text || "");
                            setEditingSpoiler(!!r.hasSpoiler);
                          }}
                        >
                          Editar
                        </button>
                        <button
                          onClick={() => {
                            closeAllKebabs();
                            handleDeleteReply(r.id);
                          }}
                        >
                          Apagar
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        {/* Composer */}
        <div className="rm-composer">
          <textarea
            ref={textareaRef}
            className={marker.marking ? "spoiler-marking" : ""}
            value={text}
            onChange={(e) => setText(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder="Faça o seu comentário aqui..."
          />
          <div className="rm-composer-controls">
            <label className="rm-check">
              <input type="checkbox" checked={replyHasSpoiler} onChange={e => setReplyHasSpoiler(e.target.checked)} />
              <span>Este comentário contém <strong>spoilers</strong> (para esconder tudo)</span>
            </label>
            <div className="rm-marker">
              {!marker.marking ? (
                <button className="rm-marker-btn" onClick={marker.start}>Marcador de Spoiler</button>
              ) : (
                <div className="rm-marker-on">
                  <span className="rm-marker-hint">use o cursor para selecionar os trechos e clique “Aplicar à seleção”</span>
                  <button className="rm-marker-finish" onClick={marker.finish}>Terminei de Marcar</button>
                  <div className="rm-marker-tools">
                    <button onClick={marker.wrapSelection}>Aplicar à seleção</button>
                    <button onClick={marker.unwrapSelection}>Remover da seleção</button>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="rm-composer-actions">
            <button className="rm-publish" onClick={handlePublishReply} disabled={submitting}>
              {submitting ? "Publicando…" : "PUBLICAR"}
            </button>
            <div className="rm-right">
              <button className="rm-close" onClick={onClose}>FECHAR</button>
              {onCloseTimeline && (
                <button className="rm-close-timeline" onClick={onCloseTimeline}>
                  FECHAR TIMELINE
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <button className="rm-backdrop" onClick={onClose} aria-label="Fechar replies" />

      {/* Popup do ReplyModal */}
      <XPopup
        open={popup.open}
        tipo={popup.tipo}
        mensagem={popup.mensagem}
        onClose={() => setPopup({ open: false, tipo: "info", mensagem: "", onConfirm: null })}
        onConfirm={popup.onConfirm}
      />
    </div>
  );
}
